import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalystBench, type PeriodView } from '../../context/AnalystBenchContext';
import { usePeriodData, useInstitutionDataAsync } from '../../hooks/useAsyncInstitutionData';
import LandingView from './views/LandingView';
import ReportsView from './views/ReportsView';
import FinancialsTable from './tables/FinancialsTable';
import KeyRatiosTable from './tables/KeyRatiosTable';
// Shared rating components from InstitutionDashboard
import RadarChart from '../camel/RadarChart/RadarChart';
import { TimelineChartContainer } from '../../pages/InstitutionDashboard/components/TimelineChart';
import { LinearGauge } from '../../pages/InstitutionDashboard/components/LinearGauge';
import { IndicatorRow } from '../../pages/InstitutionDashboard/components/IndicatorRow';
import { camelLabels } from '../../pages/InstitutionDashboard/constants';
import type { CAMELCode, HistoricalPeriod } from '../../pages/InstitutionDashboard/types';
import { transformCamelData, getFullHistory } from './utils/dataTransformers';
import { calculateQuarterAverages, formatPeriodDate } from '../../pages/InstitutionDashboard/utils';
import './ContentPanel.css';

// Per-view Period Selector Component
function ViewPeriodSelector({ viewId }: { viewId: PeriodView }) {
  const { t } = useTranslation();
  const { viewPeriods, setViewPeriod, availablePeriods } = useAnalystBench();
  const currentPeriod = viewPeriods[viewId];

  const formatPeriodLabel = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="view-period-selector">
      <label htmlFor={`period-${viewId}`}>{t('period.label')}</label>
      <select
        id={`period-${viewId}`}
        value={currentPeriod}
        onChange={(e) => setViewPeriod(viewId, e.target.value)}
        className="period-select"
      >
        {availablePeriods.map(p => (
          <option key={p} value={p}>{formatPeriodLabel(p)}</option>
        ))}
      </select>
    </div>
  );
}

export default function ContentPanel() {
  const { t } = useTranslation();
  const {
    activeSection,
    selectedInstitution,
    viewPeriods,
    financialsPeriodMode,
    setFinancialsPeriodMode,
    keyRatiosPeriodMode,
    setKeyRatiosPeriodMode,
    availablePeriods,
  } = useAnalystBench();

  // Determine which period to use based on active section
  const currentPeriod = useMemo(() => {
    switch (activeSection) {
      case 'financials':
        return viewPeriods['financials'];
      case 'key-ratios':
        return viewPeriods['key-ratios'];
      case 'quantitative-rating':
        return viewPeriods['quantitative-rating'];
      case 'credit-rating':
        return viewPeriods['credit-rating'];
      default:
        // For landing and other views, use the default (latest) period
        return viewPeriods['quantitative-rating']; // Default to quant view's period
    }
  }, [activeSection, viewPeriods]);

  // Fetch period-specific data using async hook
  const { data: periodData } = usePeriodData(
    selectedInstitution?.id,
    currentPeriod
  );

  // Fetch full institution data with history using async hook
  const { data: institutionData } = useInstitutionDataAsync(
    selectedInstitution?.id
  );

  // Convert history to format expected by TimelineChart
  const historyForTimeline = useMemo((): HistoricalPeriod[] => {
    if (!institutionData) return [];
    const fullHistory = getFullHistory(institutionData);
    return fullHistory.map((p) => ({
      date: p.date,
      rating: p.rating,
      finalScore: p.finalScore,
      camel: p.camel.map((c) => ({ code: c.code as CAMELCode, score: c.score })),
    }));
  }, [institutionData]);

  // State for timeline selection and CAMEL gauge expansion
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);

  // Get data for the timeline-selected period (for quantitative rating view)
  const selectedPeriodData = useMemo(() => {
    if (!historyForTimeline || historyForTimeline.length === 0) return null;
    return historyForTimeline[selectedPeriodIndex] || historyForTimeline[0];
  }, [historyForTimeline, selectedPeriodIndex]);

  // Transform CAMEL data for radar chart from timeline-selected period
  const camelDataForRadar = useMemo(() => {
    if (!selectedPeriodData?.camel) return [];
    return transformCamelData(selectedPeriodData.camel);
  }, [selectedPeriodData?.camel]);

  // Get previous period's score for delta display (relative to timeline selection)
  const _previousScore = useMemo(() => {
    if (!historyForTimeline || historyForTimeline.length === 0) return 0;
    // Get the period before the selected one
    const prevIndex = selectedPeriodIndex + 1;
    if (prevIndex >= historyForTimeline.length) return historyForTimeline[historyForTimeline.length - 1]?.finalScore || 0;
    return historyForTimeline[prevIndex]?.finalScore || 0;
  }, [historyForTimeline, selectedPeriodIndex]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Calculate quarter averages for comparison in gauges
  const quarterAverages = useMemo(() => {
    if (!historyForTimeline || historyForTimeline.length === 0) return { finalScore: 0, camel: {} };
    return calculateQuarterAverages(historyForTimeline, selectedPeriodIndex);
  }, [historyForTimeline, selectedPeriodIndex]);

  // Fetch period data for timeline-selected period (for indicators)
  const { data: selectedPeriodFullData } = usePeriodData(
    selectedInstitution?.id,
    selectedPeriodData?.date
  );

  // Group indicators by CAMEL code prefix of indicator_id (e.g. 'C1' → 'C', 'EG1' → 'EG', 'STI1' → 'STI')
  // This is more reliable than matching indicator_category strings which may differ across data sources
  const indicatorsByCode = useMemo(() => {
    const indicators = selectedPeriodFullData?.sankeyIndicators;
    if (!indicators) return {};
    const grouped: Record<string, typeof indicators> = {};
    indicators.forEach((ind) => {
      const code = ind.indicator_id.replace(/\d+$/, '');
      if (!grouped[code]) grouped[code] = [];
      grouped[code].push(ind);
    });
    return grouped;
  }, [selectedPeriodFullData?.sankeyIndicators]);

  // Toggle category expansion
  const toggleCategory = (code: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  // Handle period selection from timeline
  const handleSelectPeriod = (index: number) => {
    setSelectedPeriodIndex(index);
  };

  // Get rating color class
  const getRatingClass = (rating: string): string => {
    if (rating.startsWith('AA')) return 'rating-aa';
    if (rating.startsWith('A')) return 'rating-a';
    if (rating.startsWith('BBB')) return 'rating-bbb';
    if (rating.startsWith('BB')) return 'rating-bb';
    return 'rating-b';
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'landing':
        return <LandingView />;

      case 'company-profile': {
        const firstPeriod = availablePeriods.length > 0 ? availablePeriods[availablePeriods.length - 1] : null;
        const lastPeriod = availablePeriods.length > 0 ? availablePeriods[0] : null;
        return (
          <div className="content-view">
            <div className="view-header">
              <h2>{t('profile.title')}</h2>
            </div>
            <div className="view-content">
              {/* Institution Details */}
              <div className="profile-card">
                <h3 className="profile-institution-name">{selectedInstitution?.name}</h3>
                <div className="profile-divider" />
                <div className="profile-grid">
                  <div className="profile-item">
                    <span className="profile-label">{t('profile.id')}</span>
                    <span className="profile-value profile-value-mono">{selectedInstitution?.id || '--'}</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">{t('profile.type')}</span>
                    <span className="profile-value">{selectedInstitution?.type || '--'}</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">{t('profile.segment')}</span>
                    <span className="profile-value">{selectedInstitution?.segment || '--'}</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">{t('profile.country')}</span>
                    <span className="profile-value">{selectedInstitution?.country || '--'}</span>
                  </div>
                </div>
              </div>

              {/* Data Coverage */}
              <div className="profile-card">
                <h3 className="profile-section-title">{t('profile.availableData')}</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <span className="profile-label">{t('profile.details')}</span>
                    <span className="profile-value">{availablePeriods.length > 0 ? `${availablePeriods.length} months` : '--'}</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">From</span>
                    <span className="profile-value">{firstPeriod ? formatPeriodDate(firstPeriod) : '--'}</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">To</span>
                    <span className="profile-value">{lastPeriod ? formatPeriodDate(lastPeriod) : '--'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'financials':
        return (
          <div className="content-view data-table-view">
            {selectedInstitution ? (
              <FinancialsTable
                institutionId={selectedInstitution.id}
                periodMode={financialsPeriodMode}
                onPeriodModeChange={setFinancialsPeriodMode}
              />
            ) : (
              <div className="no-selection-message">
                <p>{t('financials.empty')}</p>
              </div>
            )}
          </div>
        );

      case 'key-ratios':
        return (
          <div className="content-view data-table-view">
            {selectedInstitution ? (
              <KeyRatiosTable
                institutionId={selectedInstitution.id}
                periodMode={keyRatiosPeriodMode}
                onPeriodModeChange={setKeyRatiosPeriodMode}
              />
            ) : (
              <div className="no-selection-message">
                <p>{t('ratios.empty')}</p>
              </div>
            )}
          </div>
        );

      case 'quantitative-rating':
        return (
          <div className="content-view">
            <div className="view-header">
              <div className="view-header-top">
                <h2>{t('quantitative.title')}</h2>
                {selectedPeriodData && (
                  <div className="view-period-selector">
                    <label>{t('period.label')}</label>
                    <span className="period-select-readonly">
                      {formatPeriodDate(selectedPeriodData.date)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="view-content">
              <div className="quant-new-layout">
                {/* Top Row: Timeline (left) | Performance Overview (right) */}
                <div className="quant-top-section">
                  {/* Timeline Chart */}
                  {historyForTimeline.length > 0 && selectedInstitution && (
                    <div className="timeline-card">
                      <TimelineChartContainer
                        history={historyForTimeline}
                        institution={{ id: selectedInstitution.id, name: selectedInstitution.name }}
                        selectedIndex={selectedPeriodIndex}
                        onSelectPeriod={handleSelectPeriod}
                      />
                    </div>
                  )}

                  {/* Radar Chart - Performance Overview */}
                  {camelDataForRadar.length > 0 && (
                    <div className="chart-card performance-card">
                      <h3 className="card-title">{t('camel.performanceOverview')}</h3>
                      <RadarChart camelData={camelDataForRadar} />
                    </div>
                  )}
                </div>

                {/* Bottom Row: CAMEL Indicators (3x2 grid) */}
                <div className="quant-bottom-section">
                  {/* CAMEL Indicators in 3x2 Grid */}
                  <div className="camel-grid-section">
                    <div className="camel-section-header">
                      <span className="camel-section-title">{t('camel.title')}</span>
                      <span className="camel-section-subtitle">{t('camel.subtitle')}</span>
                    </div>

                    <div className="camel-grid">
                      {selectedPeriodData?.camel.map((camelItem) => {
                        const code = camelItem.code as CAMELCode;
                        const label = camelLabels[code];
                        const indicators = indicatorsByCode[code] || [];
                        const isExpanded = expandedCategories[camelItem.code] || false;
                        const quarterAvg = quarterAverages.camel[camelItem.code] || camelItem.score;

                        return (
                          <LinearGauge
                            key={camelItem.code}
                            label={label.shortName}
                            currentValue={camelItem.score}
                            quarterAvg={quarterAvg}
                            max={label.max}
                            isExpanded={isExpanded}
                            onToggle={() => toggleCategory(camelItem.code)}
                          >
                            {indicators.map((indicator) => (
                              <IndicatorRow key={indicator.indicator_id} indicator={indicator} />
                            ))}
                          </LinearGauge>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'qualitative-rating':
        return (
          <div className="content-view">
            <div className="view-header">
              <h2>{t('qualitative.title')}</h2>
            </div>
            <div className="view-content">
              <div className="qual-sections">
                <div className="qual-section">
                  <h3>{t('qualitative.managementQuality')}</h3>
                  <div className="qual-score">
                    <span className="qual-value">{t('common.pending')}</span>
                    <span className="qual-points">{t('qualitative.pending')}</span>
                  </div>
                  <p className="qual-comment">{t('qualitative.assessmentNote')}</p>
                </div>
                <div className="qual-section">
                  <h3>{t('qualitative.governance')}</h3>
                  <div className="qual-score">
                    <span className="qual-value">{t('common.pending')}</span>
                    <span className="qual-points">{t('qualitative.pending')}</span>
                  </div>
                  <p className="qual-comment">{t('qualitative.governanceNote')}</p>
                </div>
                <div className="qual-section">
                  <h3>{t('qualitative.marketPosition')}</h3>
                  <div className="qual-score">
                    <span className="qual-value">{t('common.pending')}</span>
                    <span className="qual-points">{t('qualitative.pending')}</span>
                  </div>
                  <p className="qual-comment">{t('qualitative.positionNote')}</p>
                </div>
              </div>
              <p className="data-note">
                {t('qualitative.disclaimer')}
              </p>
            </div>
          </div>
        );

      case 'credit-rating':
        return (
          <div className="content-view">
            <div className="view-header">
              <div className="view-header-top">
                <h2>{t('credit.title')}</h2>
                <ViewPeriodSelector viewId="credit-rating" />
              </div>
            </div>
            <div className="view-content">
              <div className="credit-rating-card">
                <div className="rating-main">
                  <span className={`rating-grade ${periodData?.rating ? getRatingClass(periodData.rating) : ''}`}>
                    {periodData?.rating || '--'}
                  </span>
                  <span className="rating-outlook positive">{t('credit.stableOutlook')}</span>
                </div>
                <div className="rating-components">
                  <div className="rating-component">
                    <span className="component-label">{t('credit.quantScore')}</span>
                    <span className="component-value">{periodData?.finalScore?.toFixed(2) || '--'}</span>
                  </div>
                  <div className="rating-component">
                    <span className="component-label">{t('credit.qualAdjustment')}</span>
                    <span className="component-value">--</span>
                  </div>
                  <div className="rating-component">
                    <span className="component-label">{t('credit.finalScore')}</span>
                    <span className="component-value">{periodData?.finalScore?.toFixed(2) || '--'}</span>
                  </div>
                </div>
              </div>
              <div className="rating-scale">
                <h3>{t('credit.scale')}</h3>
                <div className="scale-items">
                  {[
                    { grade: 'AAA', range: '95-100' },
                    { grade: 'AA', range: '85-94' },
                    { grade: 'A', range: '70-84' },
                    { grade: 'BBB', range: '55-69' },
                    { grade: 'BB', range: '40-54' },
                    { grade: 'B', range: '<40' },
                  ].map(scale => (
                    <div
                      key={scale.grade}
                      className={`scale-item ${periodData?.rating?.startsWith(scale.grade) ? 'active' : ''}`}
                    >
                      <span className="scale-grade">{scale.grade}</span>
                      <span className="scale-range">{scale.range}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'reports':
        return <ReportsView />;

      default:
        return <LandingView />;
    }
  };

  return (
    <div className="content-panel">
      {renderContent()}
    </div>
  );
}
